import { ErrorHandler } from './errorHandler'

interface QueuedRequest {
  id: string
  url: string
  method: string
  data?: any
  headers?: Record<string, string>
  timestamp: number
  retryCount: number
  maxRetries: number
}

export class OfflineQueue {
  private static readonly STORAGE_KEY = 'franchise_gpt_offline_queue'
  private static readonly MAX_QUEUE_SIZE = 50
  private static readonly MAX_AGE_MS = 24 * 60 * 60 * 1000 // 24 hours
  private static queue: QueuedRequest[] = []
  private static isProcessing = false

  /**
   * Initialize offline queue
   */
  static initialize() {
    // Load queue from localStorage
    this.loadQueue()
    
    // Process queue when coming online
    window.addEventListener('online', () => {
      ErrorHandler.showInfo('Internetverbindung wiederhergestellt. Synchronisiere Daten...')
      this.processQueue()
    })
    
    // Save queue when going offline
    window.addEventListener('offline', () => {
      ErrorHandler.showWarning('Keine Internetverbindung. Ihre Aktionen werden gespeichert und später synchronisiert.')
    })
    
    // Process queue periodically
    setInterval(() => {
      if (navigator.onLine && this.queue.length > 0) {
        this.processQueue()
      }
    }, 30000) // Every 30 seconds
  }

  /**
   * Add request to queue
   */
  static enqueue(request: {
    url: string
    method: string
    data?: any
    headers?: Record<string, string>
  }): string {
    const id = crypto.randomUUID()
    const queuedRequest: QueuedRequest = {
      id,
      ...request,
      timestamp: Date.now(),
      retryCount: 0,
      maxRetries: 3,
    }
    
    // Remove old requests if queue is full
    if (this.queue.length >= this.MAX_QUEUE_SIZE) {
      this.queue.shift() // Remove oldest
    }
    
    this.queue.push(queuedRequest)
    this.saveQueue()
    
    // Show user feedback
    ErrorHandler.showInfo('Aktion wurde zur Warteschlange hinzugefügt')
    
    return id
  }

  /**
   * Process all queued requests
   */
  static async processQueue() {
    if (this.isProcessing || !navigator.onLine || this.queue.length === 0) {
      return
    }
    
    this.isProcessing = true
    const processedIds: string[] = []
    
    try {
      // Remove expired requests
      this.queue = this.queue.filter(req => 
        Date.now() - req.timestamp < this.MAX_AGE_MS
      )
      
      // Process each request
      for (const request of [...this.queue]) {
        try {
          await this.processRequest(request)
          processedIds.push(request.id)
        } catch (error) {
          // Handle individual request failure
          request.retryCount++
          
          if (request.retryCount >= request.maxRetries) {
            processedIds.push(request.id) // Remove failed request
            console.error('Request failed after max retries:', request)
          }
        }
      }
      
      // Remove processed requests
      this.queue = this.queue.filter(req => !processedIds.includes(req.id))
      this.saveQueue()
      
      if (processedIds.length > 0) {
        ErrorHandler.showSuccess(`${processedIds.length} ausstehende Aktionen wurden synchronisiert`)
      }
      
    } finally {
      this.isProcessing = false
    }
  }

  /**
   * Process a single request
   */
  private static async processRequest(request: QueuedRequest) {
    const response = await fetch(request.url, {
      method: request.method,
      headers: {
        'Content-Type': 'application/json',
        ...request.headers,
      },
      body: request.data ? JSON.stringify(request.data) : undefined,
    })
    
    if (!response.ok) {
      throw new Error(`Request failed: ${response.status}`)
    }
    
    return response.json()
  }

  /**
   * Get queue status
   */
  static getStatus(): {
    count: number
    oldestTimestamp?: number
    isProcessing: boolean
  } {
    return {
      count: this.queue.length,
      oldestTimestamp: this.queue[0]?.timestamp,
      isProcessing: this.isProcessing,
    }
  }

  /**
   * Clear queue
   */
  static clear() {
    this.queue = []
    this.saveQueue()
  }

  /**
   * Save queue to localStorage
   */
  private static saveQueue() {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.queue))
    } catch (error) {
      console.error('Failed to save offline queue:', error)
    }
  }

  /**
   * Load queue from localStorage
   */
  private static loadQueue() {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY)
      if (saved) {
        this.queue = JSON.parse(saved)
      }
    } catch (error) {
      console.error('Failed to load offline queue:', error)
      this.queue = []
    }
  }
}

// Auto-retry wrapper for critical operations
export class RetryManager {
  private static retryTimers = new Map<string, NodeJS.Timeout>()

  /**
   * Execute function with exponential backoff retry
   */
  static async withRetry<T>(
    fn: () => Promise<T>,
    options: {
      maxRetries?: number
      initialDelay?: number
      maxDelay?: number
      backoffFactor?: number
      retryIf?: (error: any) => boolean
      onRetry?: (attempt: number, delay: number) => void
    } = {}
  ): Promise<T> {
    const {
      maxRetries = 3,
      initialDelay = 1000,
      maxDelay = 30000,
      backoffFactor = 2,
      retryIf = () => true,
      onRetry,
    } = options
    
    let lastError: any
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await fn()
      } catch (error) {
        lastError = error
        
        // Check if we should retry
        if (attempt === maxRetries || !retryIf(error)) {
          throw error
        }
        
        // Calculate delay with exponential backoff
        const delay = Math.min(
          initialDelay * Math.pow(backoffFactor, attempt),
          maxDelay
        )
        
        // Notify about retry
        if (onRetry) {
          onRetry(attempt + 1, delay)
        }
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
    
    throw lastError
  }

  /**
   * Schedule a retry for a specific operation
   */
  static scheduleRetry(
    key: string,
    fn: () => Promise<void>,
    delay: number
  ) {
    // Cancel existing retry
    this.cancelRetry(key)
    
    // Schedule new retry
    const timer = setTimeout(async () => {
      try {
        await fn()
        this.retryTimers.delete(key)
      } catch (error) {
        console.error(`Retry failed for ${key}:`, error)
      }
    }, delay)
    
    this.retryTimers.set(key, timer)
  }

  /**
   * Cancel a scheduled retry
   */
  static cancelRetry(key: string) {
    const timer = this.retryTimers.get(key)
    if (timer) {
      clearTimeout(timer)
      this.retryTimers.delete(key)
    }
  }

  /**
   * Cancel all scheduled retries
   */
  static cancelAll() {
    for (const timer of this.retryTimers.values()) {
      clearTimeout(timer)
    }
    this.retryTimers.clear()
  }
}

// Initialize offline queue when module loads
if (typeof window !== 'undefined') {
  OfflineQueue.initialize()
}